import React, { useEffect, useMemo, useRef, useState } from "react";
import { Button, Typography } from "antd";
import { CheckCircleFilled, ExclamationCircleFilled, ExperimentOutlined } from "@ant-design/icons";
import CodeMirror from "@uiw/react-codemirror";
import { EditorView } from "@codemirror/view";
import { Compartment } from "@codemirror/state";
import { useSelector } from "react-redux";
import { TrainingAPI } from "@api/TrainingAPI";
import OperandBlock from "./OperandBlock";
import FormulaHelpModal from "./FormulaHelpModal";
import FormulaTestModal from "./FormulaTestModal";
import { IUDMConfig, IUDMOperand } from "./constants";
import { FormulaValidation, VariableOption } from "./types";
import {
    extractOperands,
    createConditionAutocomplete,
    createOperandLabelPlugin,
    editingOperandField,
    AutoCompleteCallbacks,
    buildMetricOptions,
    metricOnlyOperands,
    normalizeOperandLabel,
    splitMetricIdentifier,
    toQuotedFormula,
} from "./utils";
import "@styles/styles-FormulaTestModal.css";

const { Text } = Typography;

const trainingAPI = new TrainingAPI();

export interface MetricConfigBuilderProps {
    config: IUDMConfig;
    onChange: (config: IUDMConfig) => void;
    onFormulaValidation?: (valid: boolean) => void;
    currentMetricId?: number;
    decimalPlaces?: number | null;
}


const MetricConfigBuilder: React.FC<MetricConfigBuilderProps> = ({
    config,
    onChange,
    onFormulaValidation,
    currentMetricId,
    decimalPlaces,
}) => {
    const allMetricTypes = useSelector(({ team }: any) => team.all_metric_types);
    const variableOptions: VariableOption[] = useSelector(
        ({ team }: any) => team.misc?.user_defined_flag_vars,
    ) ?? [];
    const userDefinedMetrics = useSelector(({ team }: any) => team.user_defined_metrics) ?? [];
    const metricOptions = useMemo(
        () => buildMetricOptions(allMetricTypes ?? {}, userDefinedMetrics, currentMetricId),
        [allMetricTypes, userDefinedMetrics, currentMetricId],
    );
    // value_to_hit_acwr accepts only a native GPS metric that has an ACWR.
    const acwrMetricOptions = useMemo(
        () => buildMetricOptions({ gps: (allMetricTypes?.gps ?? []).filter((m: any) => m.ACWR) }),
        [allMetricTypes],
    );

    const defaultVariable = variableOptions[0]?.value || "latest";
    const defaultOperand: IUDMOperand = {
        metric_identifier: "",
        scope: "current:player",
        variable: defaultVariable,
    };

    const operands = useMemo(() => extractOperands(config.formula || ""), [config.formula]);
    const metricOnly = useMemo(() => metricOnlyOperands(config.formula || ""), [config.formula]);
    const [formulaValidation, setFormulaValidation] = useState<FormulaValidation | null>(null);
    const [validating, setValidating] = useState(false);
    const [testOpen, setTestOpen] = useState(false);

    // Display name per operand: its human label, else the selected metric's name,
    // else the raw opN token. Used to make the test modal legible.
    const operandLabels = useMemo(() => {
        const metricNameByValue = new Map<string, string>(
            metricOptions.flatMap((g) => g.options.map((o) => [o.value, o.label])),
        );
        return operands.reduce((acc, op) => {
            const params = config.fetch_params?.[op];
            const metricName = params?.metric_identifier
                ? metricNameByValue.get(splitMetricIdentifier(params.metric_identifier).base)
                : undefined;
            acc[op] = params?.label || metricName || op;
            return acc;
        }, {} as Record<string, string>);
    }, [operands, config.fetch_params, metricOptions]);

    const viewRef = useRef<EditorView | null>(null);
    const labelCompartment = useRef(new Compartment());
    const autocompleteCompartment = useRef(new Compartment());
    const pendingOperands = useRef<Record<string, { label: string; quoteChar: string }>>({});
    const autoCompleteCallbacksRef = useRef<AutoCompleteCallbacks | null>(null);
    const onLabelConfirmRef = useRef<((operandName: string, newLabel: string, from?: number, to?: number) => void) | null>(null);

    // Keep callbacks fresh every render so closures inside plugins always see latest state
    autoCompleteCallbacksRef.current = {
        getNextOperand: () => {
            const maxN = [...operands, ...Object.keys(pendingOperands.current)].reduce(
                (max, op) => Math.max(max, parseInt(op.replace("op", ""), 10) || 0),
                0,
            );
            return `op${maxN + 1}`;
        },
        setPendingLabel: (opName, label, quoteChar) => {
            pendingOperands.current[opName] = { label, quoteChar };
        },
        getExistingOperandForLabel: (label) => {
            const entries: [string, { label?: string }][] = [
                ...Object.entries(config.fetch_params || {}),
                ...Object.entries(pendingOperands.current),
            ];
            const exact = entries.find(([, p]) => p.label === label);
            if (exact) return exact[0];
            // A condition token is typed bare ("playerload"), so it must still resolve to
            // the operand labelled "Player Load" rather than creating a duplicate.
            const normalized = normalizeOperandLabel(label);
            const loose = entries.find(([, p]) => p.label && normalizeOperandLabel(p.label) === normalized);
            return loose ? loose[0] : null;
        },
        getOperandLabels: () => operandLabels,
    };

    onLabelConfirmRef.current = (operandName: string, newLabel: string, from?: number, to?: number) => {
        const currentLabel = config.fetch_params?.[operandName]?.label;
        if (newLabel === currentLabel) return;

        const formula = config.formula || "";

        // If another operand already carries this label, merge into it: replace all occurrences
        // of the current operand with that one and drop the now-redundant entry.
        const mergeTarget = Object.entries(config.fetch_params || {}).find(
            ([op, p]) => op !== operandName && p.label === newLabel,
        );
        if (mergeTarget) {
            const newFormula = formula.replace(new RegExp(`\\b${operandName}\\b`, "g"), mergeTarget[0]);
            const newFetchParams = { ...config.fetch_params };
            delete newFetchParams[operandName];
            onChange({ formula: newFormula, fetch_params: newFetchParams });
            return;
        }

        const occurrences = (formula.match(new RegExp(`\\b${operandName}\\b`, "g")) || []).length;

        if (occurrences > 1 && from !== undefined && to !== undefined) {
            // Multiple occurrences: split this specific chip into its own new operand.
            const maxN = Object.keys(config.fetch_params || {}).reduce(
                (max, op) => Math.max(max, parseInt(op.replace("op", ""), 10) || 0),
                0,
            );
            const newOpName = `op${maxN + 1}`;
            const newFormula = formula.slice(0, from) + newOpName + formula.slice(to);
            onChange({
                formula: newFormula,
                fetch_params: {
                    ...config.fetch_params,
                    [newOpName]: {
                        scope: "current:player",
                        variable: defaultVariable,
                        metric_identifier: "",
                        label: newLabel,
                    },
                },
            });
        } else {
            onChange({
                ...config,
                fetch_params: {
                    ...config.fetch_params,
                    [operandName]: { ...config.fetch_params[operandName], label: newLabel },
                },
            });
        }
    };

    // Reconfigure the label decoration plugin whenever fetch_params (and thus labels) change
    useEffect(() => {
        viewRef.current?.dispatch({
            effects: labelCompartment.current.reconfigure(
                createOperandLabelPlugin(config.fetch_params, onLabelConfirmRef),
            ),
        });
    }, [config.fetch_params]); // eslint-disable-line react-hooks/exhaustive-deps

    // Reconfigure autocomplete whenever the operand list changes
    useEffect(() => {
        viewRef.current?.dispatch({
            effects: autocompleteCompartment.current.reconfigure(
                createConditionAutocomplete(operands, autoCompleteCallbacksRef),
            ),
        });
    }, [operands]); // eslint-disable-line react-hooks/exhaustive-deps

    // Static — never rebuilt so neither compartment is accidentally reset
    const conditionExtensions = useMemo(
        () => [
            EditorView.lineWrapping,
            editingOperandField,
            labelCompartment.current.of([]),
            autocompleteCompartment.current.of([]),
        ],
        [], // eslint-disable-line react-hooks/exhaustive-deps
    );

    const handleFormulaChange = (value: string) => {
        setFormulaValidation(null);
        onFormulaValidation?.(false);
        const newOperands = extractOperands(value);
        const updatedFetchParams: Record<string, IUDMOperand> = {};
        newOperands.forEach((op) => {
            const pending = pendingOperands.current[op];
            updatedFetchParams[op] = pending
                ? {
                      scope: "current:player",
                      variable: defaultVariable,
                      metric_identifier: "",
                      label: pending.label,
                  }
                : config.fetch_params?.[op] ?? {
                      scope: "current:player",
                      variable: defaultVariable,
                      metric_identifier: "",
                  };
        });
        pendingOperands.current = {};
        onChange({ formula: value, fetch_params: updatedFetchParams });
    };

    const handleFormulaBlur = async () => {
        const formula = config.formula?.trim();
        if (!formula) {
            setFormulaValidation(null);
            onFormulaValidation?.(true);
            return;
        }
        setValidating(true);
        try {
            const sampleData = Object.keys(config.fetch_params ?? {}).reduce(
                (acc, key) => ({ ...acc, [key]: 1 }),
                {} as Record<string, number>,
            );
            const result: any = await trainingAPI.validateFormula(toQuotedFormula(formula), sampleData);
            const valid: boolean = result?.is_valid ?? false;
            setFormulaValidation({ is_valid: valid, error_message: result?.error_message });
            onFormulaValidation?.(valid);
        } catch {
            setFormulaValidation({ is_valid: false, error_message: "Validation failed." });
            onFormulaValidation?.(false);
        } finally {
            setValidating(false);
        }
    };

    // Validate any pre-existing formula on mount so the Save button reflects the
    // formula's validity when editing an existing metric — without forcing the user
    // to focus and blur the editor first.
    useEffect(() => {
        handleFormulaBlur();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const handleApplyExample = (newConfig: IUDMConfig) => {
        setFormulaValidation(null);
        onFormulaValidation?.(false);
        onChange(newConfig);
    };

    const handleOperandChange = (operandName: string, params: IUDMOperand) => {
        onChange({
            ...config,
            fetch_params: { ...config.fetch_params, [operandName]: params },
        });
    };

    const onCreateEditor = (view: EditorView) => {
        viewRef.current = view;
        view.dispatch({
            effects: [
                labelCompartment.current.reconfigure(
                    createOperandLabelPlugin(config.fetch_params, onLabelConfirmRef),
                ),
                autocompleteCompartment.current.reconfigure(
                    createConditionAutocomplete(operands, autoCompleteCallbacksRef),
                ),
            ],
        });
    };

    return (
        <div className="udm-config-builder">
            <div className="udm-config-section">
                <div className="udm-condition-label-row">
                    <Text strong className="udm-section-label">Formula</Text>
                    <FormulaHelpModal onApply={handleApplyExample} />
                    {!validating && formulaValidation !== null && (
                        formulaValidation.is_valid
                            ? <CheckCircleFilled className="udm-condition-icon udm-condition-icon--valid" />
                            : <ExclamationCircleFilled className="udm-condition-icon udm-condition-icon--invalid" />
                    )}
                    <Button
                        type="link"
                        size="small"
                        icon={<ExperimentOutlined />}
                        className="udm-test-formula-btn"
                        disabled={!config.formula?.trim() || operands.length === 0}
                        onClick={() => setTestOpen(true)}
                    >
                        Test your formula
                    </Button>
                </div>
                <Text type="secondary" className="udm-section-hint">
                    Name each value in quotes, like <code>"Top Speed"</code>, or without quotes if it has no spaces, like <code>top_speed</code>.
                </Text>
                <div className={`udm-condition-editor${formulaValidation !== null && !formulaValidation.is_valid ? " udm-condition-editor--invalid" : ""}`}>
                    <CodeMirror
                        value={config.formula || ""}
                        onChange={handleFormulaChange}
                        onBlur={handleFormulaBlur}
                        extensions={conditionExtensions}
                        basicSetup={false}
                        onCreateEditor={(view) => { onCreateEditor(view); }}
                    />
                </div>
            </div>

            {operands.length > 0 && (
                <div className="udm-config-section">
                    <Text strong className="udm-section-label">Operands</Text>
                    {operands.map((op) => (
                        <OperandBlock
                            key={op}
                            operandName={op}
                            params={config.fetch_params?.[op] ?? defaultOperand}
                            metricOptions={metricOnly.has(op) ? acwrMetricOptions : metricOptions}
                            variableOptions={variableOptions}
                            onChange={(p) => handleOperandChange(op, p)}
                            metricOnly={metricOnly.has(op)}
                        />
                    ))}
                </div>
            )}

            <FormulaTestModal
                open={testOpen}
                onClose={() => setTestOpen(false)}
                formula={config.formula || ""}
                operands={operands}
                operandLabels={operandLabels}
                decimalPlaces={decimalPlaces}
            />
        </div>
    );
};

export default MetricConfigBuilder;
